import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar, Briefcase, ShieldCheck, MessageSquare, Users, Activity,
  TrendingUp, Calculator, PlusSquare, ClipboardList, ArrowUpRight,
  AlertTriangle, CheckCircle2, Clock, Sparkles, BookCheck, Inbox,
  Building2, UserCheck, Layers,
} from "lucide-react";

/* ---------------- Building blocks ---------------- */

function KPI({ icon: Icon, label, value, trend, hint, tone = "navy", testId }) {
  const palette = {
    navy:  { bg: "bg-trital-medical-blue-soft", ic: "text-trital-navy", val: "text-slate-900" },
    red:   { bg: "bg-red-50",      ic: "text-trital-red",   val: "text-slate-900" },
    green: { bg: "bg-emerald-50",  ic: "text-emerald-600",  val: "text-slate-900" },
    amber: { bg: "bg-amber-50",    ic: "text-amber-600",    val: "text-slate-900" },
  }[tone] || { bg: "bg-slate-100", ic: "text-slate-600", val: "text-slate-900" };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-lg ${palette.bg} ${palette.ic} flex items-center justify-center`}>
          <Icon className="w-5 h-5" strokeWidth={1.8} />
        </div>
        {trend != null && (
          <span className={`text-[11px] font-bold inline-flex items-center gap-0.5 ${trend >= 0 ? "text-emerald-600" : "text-trital-red"}`}>
            <TrendingUp className={`w-3 h-3 ${trend < 0 ? "rotate-180" : ""}`} strokeWidth={2} />
            {trend >= 0 ? "+" : ""}{trend}%
          </span>
        )}
      </div>
      <div className={`mt-4 font-display font-black text-3xl tracking-tight ${palette.val}`}>{value ?? "—"}</div>
      <div className="mt-1 text-[11.5px] text-slate-500 uppercase tracking-[0.12em] font-bold">{label}</div>
      {hint && <div className="mt-2 text-[11.5px] text-slate-500 leading-snug">{hint}</div>}
    </motion.div>
  );
}

function QuickAction({ to, Icon, label, desc, tone = "navy", testId }) {
  const bg = tone === "red" ? "bg-red-50 text-trital-red" : tone === "green" ? "bg-emerald-50 text-emerald-600" : "bg-trital-medical-blue-soft text-trital-navy";
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group relative rounded-2xl bg-white border border-slate-200 p-5 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all flex gap-3"
    >
      <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold text-slate-900 leading-tight group-hover:text-trital-navy">{label}</div>
        <div className="mt-0.5 text-[12.5px] text-slate-500 leading-snug">{desc}</div>
      </div>
      <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-trital-red transition-colors shrink-0" strokeWidth={1.8} />
    </Link>
  );
}

function SectionHeader({ eyebrow, title, to, linkLabel, testId }) {
  return (
    <div className="flex items-end justify-between mb-3" data-testid={testId}>
      <div>
        <div className="overline text-trital-red">{eyebrow}</div>
        <div className="mt-1 font-display font-bold text-[20px] text-slate-900">{title}</div>
      </div>
      {to && (
        <Link to={to} className="text-[12.5px] font-semibold text-trital-navy inline-flex items-center gap-1 hover:gap-1.5 transition-all">
          {linkLabel} <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8} />
        </Link>
      )}
    </div>
  );
}

function EmptyRow({ label }) {
  return (
    <div className="text-[13px] text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-lg px-4 py-5 text-center">
      {label}
    </div>
  );
}

function ShiftRow({ s }) {
  const when = s.start_time ? new Date(s.start_time) : null;
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className="w-9 h-9 rounded-lg bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center shrink-0">
        <Calendar className="w-4 h-4" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900 text-[13px] truncate">{s.title || s.specialty}</div>
        <div className="text-[11.5px] text-slate-500 truncate">
          {s.facility_name || s.city || "—"}{s.state ? `, ${s.state}` : ""}
          {when ? ` · ${when.toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
        </div>
      </div>
      {s.hourly_rate != null && (
        <div className="text-right shrink-0">
          <div className="font-display font-bold text-trital-navy text-[14px]">${s.hourly_rate}/hr</div>
          <div className="text-[9.5px] text-slate-400 uppercase tracking-wider">{s.status}</div>
        </div>
      )}
    </div>
  );
}

function ApplicationRow({ a }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className="w-9 h-9 rounded-lg bg-red-50 text-trital-red flex items-center justify-center shrink-0">
        <Briefcase className="w-4 h-4" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900 text-[13px] truncate">{a.job_title}</div>
        <div className="text-[11.5px] text-slate-500 truncate">{a.facility_name || "—"}</div>
      </div>
      <span className={`text-[9.5px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider shrink-0 ${
        a.status === "accepted" ? "bg-emerald-50 text-emerald-700" :
        a.status === "rejected" ? "bg-red-50 text-trital-red" :
        "bg-amber-50 text-amber-700"
      }`}>{a.status || "submitted"}</span>
    </div>
  );
}

function CredentialChip({ c }) {
  const exp = c.expiration_date ? new Date(c.expiration_date) : null;
  const days = exp ? Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const state = c.status || (days != null && days < 30 ? "expiring" : "verified");
  const pal = state === "approved" || state === "verified" ? "bg-emerald-50 text-emerald-700" :
              state === "expiring" ? "bg-amber-50 text-amber-700" :
              state === "rejected" ? "bg-red-50 text-trital-red" :
              "bg-slate-100 text-slate-600";
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <div className="w-9 h-9 rounded-lg bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center shrink-0">
        <ShieldCheck className="w-4 h-4" strokeWidth={1.8} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900 text-[13px] truncate">{c.type}</div>
        <div className="text-[11.5px] text-slate-500 truncate">
          {c.state ? `${c.state} · ` : ""}{exp ? `expires ${exp.toLocaleDateString()}` : "no expiry logged"}
        </div>
      </div>
      <span className={`text-[9.5px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider shrink-0 ${pal}`}>{state}</span>
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function DashboardHome() {
  const { user } = useAuth();
  const role = user?.role || "clinician";

  // Universal + role-specific data
  const [publicStats, setPublicStats] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [threads, setThreads] = useState([]);

  // Clinician
  const [myShifts, setMyShifts] = useState([]);
  const [openShifts, setOpenShifts] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [myCreds, setMyCreds] = useState([]);
  const [mySkills, setMySkills] = useState([]);

  // Facility
  const [myJobs, setMyJobs] = useState([]);

  // Admin/recruiter
  const [adminStats, setAdminStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      setLoading(true);
      const safe = (p) => p.then((r) => r.data).catch(() => null);
      try {
        // Common
        const [pub, notif, thr] = await Promise.all([
          safe(api.get("/public/stats")),
          safe(api.get("/notifications")),
          safe(api.get("/messages/threads")),
        ]);
        if (cancelled) return;
        setPublicStats(pub || {});
        setNotifications(notif || []);
        setThreads(thr || []);

        if (role === "clinician") {
          const [shifts, apps, creds, skills] = await Promise.all([
            safe(api.get("/shifts", { params: { limit: 50 } })),
            safe(api.get("/applications/mine")),
            safe(api.get("/credentials/mine")),
            safe(api.get("/skills/mine")),
          ]);
          if (cancelled) return;
          const all = shifts || [];
          setMyShifts(all.filter((s) => s.accepted_by === user?.id));
          setOpenShifts(all.filter((s) => s.status === "open").slice(0, 5));
          setMyApps(apps || []);
          setMyCreds(creds || []);
          setMySkills(skills || []);
        } else if (role === "facility") {
          const [shifts, jobs, creds] = await Promise.all([
            safe(api.get("/shifts")),
            safe(api.get("/jobs/mine")),
            safe(api.get("/credentials")),
          ]);
          if (cancelled) return;
          setMyShifts(shifts || []);
          setMyJobs(jobs || []);
          setMyCreds(creds || []);
        } else if (role === "recruiter") {
          const [jobs, leads, creds] = await Promise.all([
            safe(api.get("/jobs")),
            safe(api.get("/admin/leads", { params: { status: "new" } })),
            safe(api.get("/credentials")),
          ]);
          if (cancelled) return;
          setMyJobs(jobs || []);
          setRecentLeads(leads || []);
          setMyCreds(creds || []);
        } else if (role === "admin") {
          const [stats, leads] = await Promise.all([
            safe(api.get("/admin/analytics")),
            safe(api.get("/admin/leads")),
          ]);
          if (cancelled) return;
          setAdminStats(stats);
          setRecentLeads(leads || []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (user?.id) loadAll();
    return () => { cancelled = true; };
  }, [user?.id, role]);

  const firstName = useMemo(() => (user?.name || "there").split(" ")[0], [user?.name]);

  const expiringCreds = useMemo(() => {
    const now = Date.now();
    return (myCreds || []).filter((c) => {
      if (!c.expiration_date) return false;
      const diff = (new Date(c.expiration_date).getTime() - now) / (1000 * 60 * 60 * 24);
      return diff <= 30 && diff >= -0;
    });
  }, [myCreds]);

  /* ---------------- Render ---------------- */
  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Greeting band */}
      <div className="rounded-3xl border border-slate-200 bg-white shadow-card relative overflow-hidden" data-testid="dash-greeting">
        <div className="absolute inset-0 bg-trital-mesh opacity-60 pointer-events-none" />
        <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-trital-medical-blue-soft blur-3xl pointer-events-none" />
        <div className="relative p-6 lg:p-8 grid lg:grid-cols-[1fr_auto] items-center gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 shadow-card">
              <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-navy">Command center · {role}</span>
            </div>
            <h1 className="mt-3 font-display font-black text-4xl lg:text-5xl text-slate-900 tracking-tight leading-tight">
              Welcome back, <span className="text-trital-navy">{firstName}.</span>
            </h1>
            <p className="mt-2 text-slate-600 text-[14px] max-w-2xl">
              {role === "clinician"   && "Your assignments, credentials, and new opportunities — at a glance."}
              {role === "facility"    && "Fill status, requisition pipeline, and clinician supply — in one view."}
              {role === "recruiter"   && "Active jobs, new candidate leads, and credential queues — centralized."}
              {role === "admin"       && "Platform health, user moderation, and leads CRM — command-level view."}
            </p>
          </div>
          {expiringCreds.length > 0 && role === "clinician" && (
            <Link to="/dashboard/credentials" data-testid="dash-cred-alert" className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 hover:bg-amber-100 transition-colors">
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" strokeWidth={1.8} />
              <div>
                <div className="font-display font-bold text-amber-900 text-[14px]">{expiringCreds.length} credential{expiringCreds.length > 1 ? "s" : ""} expiring</div>
                <div className="text-[12px] text-amber-800 mt-0.5">Renew within 30 days to stay eligible for shifts.</div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-amber-700 shrink-0 mt-1" strokeWidth={1.8} />
            </Link>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dash-kpis">
        {role === "clinician" && (<>
          <KPI icon={Calendar}    label="Upcoming shifts"   value={myShifts.length}          tone="navy"   hint="Accepted & in progress"  testId="kpi-upcoming-shifts" />
          <KPI icon={Briefcase}   label="Applications"      value={myApps.length}            tone="red"    hint="Active + in-review"     testId="kpi-applications" />
          <KPI icon={ShieldCheck} label="Credentials"       value={myCreds.length}           tone="green"  hint={expiringCreds.length ? `${expiringCreds.length} expiring` : "All current"} testId="kpi-credentials" />
          <KPI icon={MessageSquare} label="Message threads" value={threads.length}           tone="amber"  hint={`${threads.filter((t) => t.unread).length || 0} unread`} testId="kpi-threads" />
        </>)}
        {role === "facility" && (<>
          <KPI icon={Calendar}    label="Open shifts"       value={(myShifts.filter((s) => s.status === "open") || []).length} tone="red"   testId="kpi-open-shifts" />
          <KPI icon={UserCheck}   label="Filled this week"  value={(myShifts.filter((s) => s.status !== "open") || []).length} tone="green" testId="kpi-filled" />
          <KPI icon={Briefcase}   label="Jobs live"         value={(myJobs.filter((j) => j.status === "open") || []).length}  tone="navy"  testId="kpi-jobs-live" />
          <KPI icon={Layers}      label="Credential queue"  value={(myCreds.filter((c) => c.status === "submitted") || []).length} tone="amber" testId="kpi-cred-queue" />
        </>)}
        {role === "recruiter" && (<>
          <KPI icon={Briefcase}   label="Jobs to source"    value={(myJobs.filter((j) => j.status === "open") || []).length}  tone="navy"  testId="kpi-jobs-to-source" />
          <KPI icon={Inbox}       label="New leads"         value={recentLeads.length}                                        tone="red"   testId="kpi-new-leads" />
          <KPI icon={ShieldCheck} label="Credential queue"  value={(myCreds.filter((c) => c.status === "submitted") || []).length} tone="amber" testId="kpi-cred-queue" />
          <KPI icon={MessageSquare} label="Threads"         value={threads.length}                                            tone="green" testId="kpi-threads" />
        </>)}
        {role === "admin" && (<>
          <KPI icon={Users}       label="Active clinicians" value={adminStats?.active_clinicians}                             tone="green" testId="kpi-active-clinicians" />
          <KPI icon={Calendar}    label="Open shifts"       value={adminStats?.open_shifts}                                    tone="red"   testId="kpi-open-shifts" />
          <KPI icon={Briefcase}   label="Open jobs"         value={adminStats?.jobs_open}                                      tone="navy"  testId="kpi-open-jobs" />
          <KPI icon={TrendingUp}  label="Placement rate"    value={adminStats?.placement_rate != null ? `${adminStats.placement_rate}%` : "—"} tone="amber" testId="kpi-placement-rate" />
        </>)}
      </div>

      {/* Quick actions */}
      <div data-testid="dash-quick-actions">
        <SectionHeader eyebrow="Quick actions" title="What needs your attention today" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {role === "clinician" && (<>
            <QuickAction to="/dashboard/shifts"      Icon={Calendar}    label="Browse PRN shifts"  desc={`${publicStats.shifts ?? "—"} shifts open nationwide`} testId="qa-shifts" />
            <QuickAction to="/dashboard/jobs"        Icon={Briefcase}   label="Find jobs"          desc="Travel · local · permanent" tone="red" testId="qa-jobs" />
            <QuickAction to="/dashboard/calculator"  Icon={Calculator}  label="Travel calculator"  desc="Compare offers · GSA math" tone="green" testId="qa-calculator" />
            <QuickAction to="/dashboard/credentials" Icon={ShieldCheck} label="My credentials"     desc="License · certs · audit log" testId="qa-credentials" />
          </>)}
          {role === "facility" && (<>
            <QuickAction to="/dashboard/post-shift"    Icon={PlusSquare}    label="Post PRN shift"    desc="Fill gaps fast" testId="qa-post-shift" />
            <QuickAction to="/dashboard/post-job"      Icon={PlusSquare}    label="Post a job"        desc="Travel · local · permanent" tone="red" testId="qa-post-job" />
            <QuickAction to="/dashboard/manage-shifts" Icon={ClipboardList} label="Manage shifts"     desc="Track fill rate, moderate" tone="green" testId="qa-manage-shifts" />
            <QuickAction to="/dashboard/directory"     Icon={Users}         label="Clinician directory" desc="Search, submit, bookmark" testId="qa-directory" />
          </>)}
          {role === "recruiter" && (<>
            <QuickAction to="/dashboard/directory"   Icon={Users}       label="Clinician directory" desc="Source talent, track pipeline" testId="qa-directory" />
            <QuickAction to="/dashboard/credentials" Icon={ShieldCheck} label="Credential queue"    desc="Approve / reject" tone="amber" testId="qa-credentials" />
            <QuickAction to="/dashboard/post-job"    Icon={PlusSquare}  label="Post a job"          desc="Open new requisition" tone="red" testId="qa-post-job" />
            <QuickAction to="/dashboard/messages"    Icon={MessageSquare} label="Candidate messages" desc="Reply inside 1 business day" tone="green" testId="qa-messages" />
          </>)}
          {role === "admin" && (<>
            <QuickAction to="/dashboard/admin"       Icon={Activity}    label="Platform analytics" desc="Revenue, placements, SLA" testId="qa-analytics" />
            <QuickAction to="/dashboard/directory"   Icon={Users}       label="Users"              desc="Approve · moderate" tone="red" testId="qa-users" />
            <QuickAction to="/dashboard/credentials" Icon={ShieldCheck} label="Compliance queue"   desc="Approve credential docs" tone="amber" testId="qa-credentials" />
            <QuickAction to="/dashboard/shifts"      Icon={Calendar}    label="Moderate shifts"    desc="Flagged shifts · SLAs" tone="green" testId="qa-shifts" />
          </>)}
        </div>
      </div>

      {/* Activity grid */}
      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6" data-testid="dash-activity">
        {/* Primary column */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card">
          {role === "clinician" && (<>
            <SectionHeader eyebrow="Next up" title="Your upcoming shifts" to="/dashboard/shifts" linkLabel="Open marketplace" />
            {myShifts.length ? myShifts.slice(0, 6).map((s) => <ShiftRow key={s.id} s={s} />) : <EmptyRow label="No accepted shifts yet. Browse the PRN marketplace to accept your first." />}

            <div className="mt-6"><SectionHeader eyebrow="Recommended for you" title="New open shifts" to="/dashboard/shifts" linkLabel="See all" /></div>
            {openShifts.length ? openShifts.map((s) => <ShiftRow key={s.id} s={s} />) : <EmptyRow label="No matching open shifts right now — check back soon." />}
          </>)}

          {role === "facility" && (<>
            <SectionHeader eyebrow="Fill status" title="Recent shifts you posted" to="/dashboard/manage-shifts" linkLabel="Manage" />
            {myShifts.length ? myShifts.slice(0, 8).map((s) => <ShiftRow key={s.id} s={s} />) : <EmptyRow label="No shifts posted yet. Publish your first requisition." />}
          </>)}

          {role === "recruiter" && (<>
            <SectionHeader eyebrow="Requisitions" title="Open jobs needing coverage" to="/dashboard/jobs" linkLabel="See all" />
            {myJobs.length ? myJobs.slice(0, 6).map((j) => (
              <div key={j.id} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
                <div className="w-9 h-9 rounded-lg bg-red-50 text-trital-red flex items-center justify-center shrink-0"><Briefcase className="w-4 h-4" strokeWidth={1.8} /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 text-[13px] truncate">{j.title}</div>
                  <div className="text-[11.5px] text-slate-500 truncate">{j.facility_name} · {j.city}, {j.state}</div>
                </div>
                <span className="text-[9.5px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase tracking-wider shrink-0">{j.job_type || j.type}</span>
              </div>
            )) : <EmptyRow label="No open jobs right now." />}
          </>)}

          {role === "admin" && (<>
            <SectionHeader eyebrow="Platform pulse" title="Recent leads" to="/dashboard/admin" linkLabel="Open analytics" />
            {recentLeads.length ? recentLeads.slice(0, 8).map((l) => (
              <div key={l.id} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
                <div className="w-9 h-9 rounded-lg bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center shrink-0"><Inbox className="w-4 h-4" strokeWidth={1.8} /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 text-[13px] truncate">{l.name || l.email} <span className="text-slate-400 font-normal">· {l.source}</span></div>
                  <div className="text-[11.5px] text-slate-500 truncate">{l.need || l.notes || l.email}</div>
                </div>
                <span className="text-[9.5px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase tracking-wider shrink-0">{l.status || "new"}</span>
              </div>
            )) : <EmptyRow label="No leads yet." />}

            {adminStats && (
              <div className="mt-6 grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Revenue (weekly est.)</div>
                  <div className="mt-1 font-display font-bold text-slate-900 text-[18px]">${Math.round(adminStats.revenue_weekly || 0).toLocaleString()}</div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Placements</div>
                  <div className="mt-1 font-display font-bold text-slate-900 text-[18px]">{adminStats.placements ?? "—"}</div>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Facilities</div>
                  <div className="mt-1 font-display font-bold text-slate-900 text-[18px]">{adminStats.facilities ?? "—"}</div>
                </div>
              </div>
            )}
          </>)}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {role === "clinician" && (<>
            <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card">
              <SectionHeader eyebrow="Applications" title="Active applications" to="/dashboard/jobs" linkLabel="Jobs" />
              {myApps.length ? myApps.slice(0, 6).map((a) => <ApplicationRow key={a.id} a={a} />) : <EmptyRow label="No applications yet." />}
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card">
              <SectionHeader eyebrow="Compliance" title="Credential status" to="/dashboard/credentials" linkLabel="Manage" />
              {myCreds.length ? myCreds.slice(0, 6).map((c) => <CredentialChip key={c.id} c={c} />) : <EmptyRow label="Upload your license and certifications to become eligible." />}
            </div>
          </>)}

          {role === "facility" && (
            <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card">
              <SectionHeader eyebrow="Requisitions" title="Open jobs" to="/dashboard/jobs" linkLabel="See all" />
              {myJobs.length ? myJobs.slice(0, 6).map((j) => (
                <div key={j.id} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
                  <div className="w-9 h-9 rounded-lg bg-red-50 text-trital-red flex items-center justify-center"><Briefcase className="w-4 h-4" strokeWidth={1.8}/></div>
                  <div className="min-w-0 flex-1"><div className="font-semibold text-slate-900 text-[13px] truncate">{j.title}</div><div className="text-[11.5px] text-slate-500 truncate">{j.job_type || j.type}</div></div>
                  <span className="text-[9.5px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase tracking-wider">{j.status}</span>
                </div>
              )) : <EmptyRow label="No jobs posted yet." />}
            </div>
          )}

          {(role === "recruiter" || role === "admin") && (
            <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card">
              <SectionHeader eyebrow="CRM" title="Latest leads" to={role === "admin" ? "/dashboard/admin" : "/dashboard/directory"} linkLabel="Open" />
              {recentLeads.length ? recentLeads.slice(0, 6).map((l) => (
                <div key={l.id} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0">
                  <div className="w-9 h-9 rounded-lg bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center shrink-0"><Building2 className="w-4 h-4" strokeWidth={1.8}/></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 text-[13px] truncate">{l.organization || l.name}</div>
                    <div className="text-[11.5px] text-slate-500 truncate">{l.source} · {l.need || "—"}</div>
                  </div>
                  <span className="text-[9.5px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase tracking-wider shrink-0">{l.status || "new"}</span>
                </div>
              )) : <EmptyRow label="No leads yet." />}
            </div>
          )}

          {/* Always-on: notifications */}
          <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card" data-testid="dash-notifications">
            <SectionHeader eyebrow="Notifications" title="What you missed" />
            {notifications.length ? (
              <ul className="space-y-2">
                {notifications.slice(0, 6).map((n) => (
                  <li key={n.id} className="flex items-start gap-3 py-1.5">
                    <span className={`mt-1.5 w-2 h-2 rounded-full ${n.type === "warning" ? "bg-amber-500" : n.type === "success" ? "bg-emerald-500" : "bg-trital-navy"} shrink-0`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-slate-800 truncate">{n.title}</div>
                      {n.body && <div className="text-[11.5px] text-slate-500 leading-snug">{n.body}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            ) : <EmptyRow label="Nothing new. You're all caught up." />}
          </div>

          {/* Clinician bonus: skills */}
          {role === "clinician" && mySkills.length > 0 && (
            <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card">
              <SectionHeader eyebrow="Skills" title="Top self-rated competencies" to="/dashboard/skills" linkLabel="Rate more" />
              <div className="flex flex-wrap gap-1.5 pt-1">
                {mySkills.slice(0, 12).map((sk) => (
                  <span key={sk.id} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy text-[11px] font-semibold">
                    <BookCheck className="w-3 h-3" strokeWidth={1.8} />
                    {sk.skill_name} · {sk.self_rating}/5
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-center text-[12px] text-slate-400 flex items-center justify-center gap-1.5" data-testid="dash-loading">
          <Clock className="w-3 h-3 animate-spin" strokeWidth={1.8} /> Loading the latest data…
        </div>
      )}

      {/* Footer hint */}
      <div className="flex flex-wrap items-center gap-2 pt-2 text-[12px] text-slate-500" data-testid="dash-footer-hint">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" strokeWidth={1.8} />
        Data refreshes the moment you land on this page. Use quick actions above to jump directly into any flow.
      </div>
    </div>
  );
}
